import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Collapse,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close,
  History,
  OpenInNew,
  ExpandMore,
  ExpandLess,
  CheckCircleOutline,
  RadioButtonUnchecked,
  Person,
  CalendarMonth,
  Assignment,
  Science,
  LocalHospital,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getPatientHistory } from '../../services/studies';
import { generateOHIFUrl, calculateAge } from '../../services/utils';

const MODALITY_COLORS = {
  CT: '#2196F3',
  MR: '#9C27B0',
  US: '#00897B',
  XR: '#F57C00',
  CR: '#F57C00',
  DR: '#F57C00',
  MG: '#E91E63',
  PT: '#FF5722',
  NM: '#8D6E63',
  OT: '#607D8B',
  DX: '#F57C00',
  PR: '#607D8B',
};

function StudyRow({ s, currentStudyId }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(false);

  const isCurrent = s._id === currentStudyId;
  const hasImages = s.totalInstances > 0 || s.instanceCount > 0;
  const isReported = s.selfReportStatus === 'completed' || s.dycovueReportStatus === 'completed' || !!s.report;
  const modalityColor = MODALITY_COLORS[s.modality] || '#607D8B';

  const studyDateStr = s.studyDate
    ? new Date(s.studyDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : (s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

  const handleOpenViewer = () => {
    const uid = s.studyInstanceUID || s.orthancStudyId;
    if (!uid) return;
    const base = process.env.REACT_APP_OHIF_URL || 'http://localhost:8042/ohif';
    const url = generateOHIFUrl(uid, base);
    const w = window.open(url, '_blank', 'noopener,noreferrer,width=1400,height=900');
    if (w) { w.opener = null; try { w.focus(); } catch (_) {} }
  };

  const borderColor = isCurrent
    ? (isDark ? 'rgba(0,102,92,0.5)' : 'rgba(0,102,92,0.3)')
    : (isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0');
  const rowBg = isCurrent
    ? (isDark ? 'rgba(0,102,92,0.12)' : 'rgba(0,102,92,0.05)')
    : (isDark ? '#1E1E1E' : '#FFFFFF');

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        mb: 1.5,
        bgcolor: rowBg,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: expanded ? 0.5 : 1.5 }}>
        {/* Top row: date + modality + current badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Chip
            label={s.modality || 'OT'}
            size="small"
            sx={{
              bgcolor: modalityColor,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 22,
              minWidth: 36,
            }}
          />
          <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 500 }}>
            {studyDateStr}
          </Typography>
          {isCurrent && (
            <Chip label="Current" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem', ml: 'auto' }} />
          )}
        </Box>

        {/* Study description */}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#0F172A', mb: 0.4, fontSize: '0.88rem' }}
        >
          {s.studyDescription || 'Imaging Study'}
        </Typography>

        {/* Accession + facility row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
          {s.accessionNumber && (
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              ACC: {s.accessionNumber}
            </Typography>
          )}
          {s.facility?.facilityName && (
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              {s.facility.facilityName}
            </Typography>
          )}
        </Box>

        {/* Report status + actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            {isReported
              ? <CheckCircleOutline sx={{ fontSize: 14, color: '#22C55E' }} />
              : <RadioButtonUnchecked sx={{ fontSize: 14, color: isDark ? '#64748B' : '#94A3B8' }} />
            }
            <Typography variant="caption" sx={{ color: isReported ? '#22C55E' : (isDark ? '#64748B' : '#94A3B8'), fontWeight: 500 }}>
              {isReported ? 'Reported' : 'Pending'}
            </Typography>
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            {hasImages && (
              <Tooltip title="Open in OHIF Viewer">
                <IconButton size="small" onClick={handleOpenViewer} sx={{ color: '#00665C' }}>
                  <OpenInNew sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={expanded ? 'Collapse' : 'View details'}>
              <IconButton size="small" onClick={() => setExpanded(e => !e)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Expanded section */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5, bgcolor: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)' }}>
          {s.report?.impression ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#CBD5E1' : '#334155', display: 'block', mb: 0.4 }}>
                IMPRESSION
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#475569', fontSize: '0.82rem', lineHeight: 1.6 }}>
                {s.report.impression}
              </Typography>
              {s.report.radiologist && (
                <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8', display: 'block', mt: 1 }}>
                  Reported by: {s.report.radiologist.name || `${s.report.radiologist.firstName || ''} ${s.report.radiologist.lastName || ''}`.trim()}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8', fontStyle: 'italic' }}>
              No report impression available for this study.
            </Typography>
          )}

          {/* Extra metadata */}
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {s.institutionName && (
              <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
                Institution: {s.institutionName}
              </Typography>
            )}
            {s.totalInstances > 0 && (
              <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
                {s.totalInstances} image{s.totalInstances !== 1 ? 's' : ''}
              </Typography>
            )}
            {s.uploadedBy && (
              <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
                By: {s.uploadedBy.firstName} {s.uploadedBy.lastName}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function LabOrderRow({ order }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(false);

  const orderDateStr = order.createdAt
    ? new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const testNames = (order.orderItems || [])
    .map(i => i.testName || i.test?.testName)
    .filter(Boolean)
    .join(', ') || 'Laboratory Diagnostic Test';

  const status = order.status || 'ORDERED';
  const isCompleted =
    status === 'COMPLETED' ||
    status === 'RESULT_ENTERED' ||
    status === 'RESULT_RELEASED' ||
    status === 'RESULT_VERIFIED';

  const statusColor = isCompleted
    ? '#22C55E'
    : status === 'IN_PROGRESS' || status === 'SAMPLE_RECEIVED' || status === 'RECEIVED'
      ? '#3B82F6'
      : '#F59E0B';

  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';
  const rowBg = isDark ? '#1E1E1E' : '#FFFFFF';

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        mb: 1.5,
        bgcolor: rowBg,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: expanded ? 0.5 : 1.5 }}>
        {/* Top row: date + LAB badge + priority */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Chip
            label="LAB"
            size="small"
            sx={{
              bgcolor: '#7C3AED',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 22,
              minWidth: 36,
            }}
          />
          <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 500 }}>
            {orderDateStr}
          </Typography>
          {order.priority === 'STAT' && (
            <Chip label="STAT" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
          )}
          <Box sx={{ ml: 'auto' }}>
            <Chip
              label={status.replace(/_/g, ' ')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                color: statusColor,
                border: `1px solid ${statusColor}40`
              }}
            />
          </Box>
        </Box>

        {/* Test description */}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#0F172A', mb: 0.4, fontSize: '0.88rem' }}
        >
          {testNames}
        </Typography>

        {/* Accession + Order ID + Facility */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
          {order.orderId && (
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              ORD: {order.orderId}
            </Typography>
          )}
          {order.specimens?.[0]?.specimenId && (
            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
              Sample: {order.specimens[0].specimenId}
            </Typography>
          )}
          {order.facility?.facilityName && (
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              {order.facility.facilityName}
            </Typography>
          )}
        </Box>

        {/* Expand action */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}>
          <Tooltip title={expanded ? 'Collapse' : 'View test details'}>
            <IconButton size="small" onClick={() => setExpanded(e => !e)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
              {expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Expanded section */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5, bgcolor: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#CBD5E1' : '#334155', display: 'block', mb: 0.8 }}>
            ORDERED TESTS & SPECIMENS
          </Typography>
          {(order.orderItems || []).map((item, idx) => (
            <Box key={idx} sx={{ mb: 1, p: 1, borderRadius: '6px', bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC', border: `1px solid ${borderColor}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#0F172A', fontSize: '0.82rem' }}>
                  {item.testName}
                </Typography>
                <Chip label={item.status || status} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                {item.specimenType && (
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                    Type: {item.specimenType}
                  </Typography>
                )}
                {item.departmentName && (
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                    Dept: {item.departmentName}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}

          {order.requestingDoctor && (
            <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8', display: 'block', mt: 1 }}>
              Requesting Doctor: {order.requestingDoctor}
            </Typography>
          )}
          {order.clinicalNotes && (
            <Typography variant="caption" sx={{ color: isDark ? '#64748B' : '#94A3B8', display: 'block', mt: 0.5 }}>
              Clinical Notes: {order.clinicalNotes}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function PatientHistoryPanel({
  open,
  onClose,
  patientId,
  patientName,
  patientBirthDate,
  patientSex,
  currentStudyId
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [studies, setStudies] = useState([]);
  const [labOrders, setLabOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categoryTab, setCategoryTab] = useState(0); // 0 = All, 1 = Radiology, 2 = Laboratory

  useEffect(() => {
    if (!open || !patientId) return;
    setLoading(true);
    setError('');
    getPatientHistory(patientId)
      .then(data => {
        setStudies(data.studies || []);
        setLabOrders(data.labOrders || []);
      })
      .catch(() => setError('Failed to load patient history.'))
      .finally(() => setLoading(false));
  }, [open, patientId]);

  const age = patientBirthDate ? `${calculateAge(patientBirthDate)}y` : null;
  const displayName = patientName ? patientName.replace(/\^/g, ' ').trim() : patientId;

  const totalCount = studies.length + labOrders.length;
  const drawerBg = isDark ? '#121212' : '#F8FAFC';
  const headerBg = isDark ? '#1E1E1E' : '#FFFFFF';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          bgcolor: drawerBg,
          backgroundImage: 'none',
        }
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: headerBg, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`, px: 2.5, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History sx={{ color: '#00665C', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: isDark ? '#F1F5F9' : '#0F172A' }}>
              Patient Diagnostic History
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Patient info */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person sx={{ fontSize: 16, color: isDark ? '#94A3B8' : '#64748B' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#0F172A' }}>
              {displayName}
            </Typography>
          </Box>
          {patientId && (
            <Chip label={`MRN: ${patientId}`} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
          )}
          {patientSex && (
            <Chip label={patientSex === 'M' ? 'Male' : patientSex === 'F' ? 'Female' : patientSex} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />
          )}
          {age && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <CalendarMonth sx={{ fontSize: 14, color: isDark ? '#94A3B8' : '#64748B' }} />
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>{age}</Typography>
            </Box>
          )}
        </Box>

        {/* Category Tabs */}
        <Tabs
          value={categoryTab}
          onChange={(e, val) => setCategoryTab(val)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              px: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              color: isDark ? '#94A3B8' : '#64748B',
              "&.Mui-selected": { color: '#00665C' }
            },
            "& .MuiTabs-indicator": { bgcolor: '#00665C', height: 3 }
          }}
        >
          <Tab label={`All (${totalCount})`} />
          <Tab icon={<LocalHospital sx={{ fontSize: 16 }} />} iconPosition="start" label={`Radiology (${studies.length})`} />
          <Tab icon={<Science sx={{ fontSize: 16 }} />} iconPosition="start" label={`Laboratory (${labOrders.length})`} />
        </Tabs>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress size={32} sx={{ color: '#00665C' }} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {!loading && !error && totalCount === 0 && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Assignment sx={{ fontSize: 48, color: isDark ? '#334155' : '#CBD5E1', mb: 1.5 }} />
            <Typography variant="body2" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
              No diagnostic tests or imaging studies found for this patient.
            </Typography>
          </Box>
        )}

        {!loading && !error && totalCount > 0 && (
          <>
            {/* Tab 0: ALL */}
            {categoryTab === 0 && (
              <>
                {studies.map(s => (
                  <StudyRow key={`study-${s._id}`} s={s} currentStudyId={currentStudyId} />
                ))}
                {labOrders.map(o => (
                  <LabOrderRow key={`lab-${o._id}`} order={o} />
                ))}
              </>
            )}

            {/* Tab 1: Radiology */}
            {categoryTab === 1 && (
              studies.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
                    No radiology imaging studies found.
                  </Typography>
                </Box>
              ) : (
                studies.map(s => (
                  <StudyRow key={`study-${s._id}`} s={s} currentStudyId={currentStudyId} />
                ))
              )
            )}

            {/* Tab 2: Laboratory */}
            {categoryTab === 2 && (
              labOrders.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Typography variant="body2" sx={{ color: isDark ? '#64748B' : '#94A3B8' }}>
                    No laboratory test orders found.
                  </Typography>
                </Box>
              ) : (
                labOrders.map(o => (
                  <LabOrderRow key={`lab-${o._id}`} order={o} />
                ))
              )
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}
